import { computed } from 'vue'
import type { Component } from 'vue'
import { useRoute } from 'vue-router'
import {
  Users,
  ShieldCheck,
  UsersRound,
  MapPin,
  CalendarClock,
  ListChecks,
  FileSignature,
  HelpCircle,
  ClipboardList,
  Mail,
  Bell,
  CalendarArrowUp,
  DatabaseBackup,
  Plug,
  ScrollText,
  LockKeyhole,
  Receipt,
  FileText,
  Hospital
} from 'lucide-vue-next'
import { usePermissions } from '@/composables/usePermissions'
import { Permission, type PermissionValue } from '@/utils/permissionBitmask'
import type { OrganizationCountKey } from '@/composables/useOrganizationOverview'

/**
 * Organisatiebeheer's structure — the management areas, grouped.
 *
 * **This is the one declaration.** It is rendered twice: as the hub's grouped
 * cards (`OrganizationView`, #4365) and as the settings sidebar in `SideMenu`
 * (#4368). Both read the same array through the same permission filter, so a
 * group a user may not have disappears from both at once, and an area cannot be
 * offered in one place and missing from the other.
 *
 * It lives here rather than in `OrganizationView.vue` for exactly that reason:
 * the sidebar renders on every settings screen, and a hub that owned the array
 * would have to be imported by the sidebar to reach it.
 *
 * Shaped after `useDossierContext` (#4256), which answers the same three
 * questions for a client dossier: which items does this context offer, which
 * routes does it own, and which item is the open one.
 */

/** The five headings, in render order. Order is taken from `ORGANIZATION_AREAS`. */
export type OrganizationGroupId = 'organization' | 'people' | 'product' | 'dossier' | 'imports'

/** One management area — a card on the hub and an item in the sidebar. */
export interface OrganizationArea {
  /** Stable key, used for `data-testid`s and as the active marker. */
  id: string
  group: OrganizationGroupId
  icon: Component
  titleKey: string
  descriptionKey: string
  routeName: string
  /** Pages below this area that keep it marked as the current one. */
  extraRoutes?: readonly string[]
  /** When set, the area is only offered to a user holding it — or any of them, for a list. */
  permission?: PermissionValue | readonly PermissionValue[]
  /** Count badge on the hub. The sidebar carries no badge. */
  countKey?: OrganizationCountKey
}

export const ORGANIZATION_GROUP_TITLE_KEYS: Readonly<Record<OrganizationGroupId, string>> = {
  organization: 'organization.groups.organization',
  people: 'organization.groups.people',
  product: 'organization.groups.product',
  dossier: 'organization.groups.dossier',
  imports: 'organization.groups.imports'
}

/**
 * The areas, in render order — groups appear in the order their first
 * area appears here.
 *
 * Rollenbeheer sits with Gebruikers and Teams on purpose: a role is permissions,
 * while a designation (is_behandelaar / is_regiebehandelaar) lives on the
 * membership and grants nothing (#3434). One heading about access is the
 * cheapest place to make the two read as different things. The Auditlog joins
 * them as the record of what that access was used for.
 */
export const ORGANIZATION_AREAS: readonly OrganizationArea[] = [
  {
    // No permission: every member reads it, and only editing asks for one.
    id: 'practiceDetails',
    group: 'organization',
    icon: Hospital,
    titleKey: 'organization.sections.practiceDetails.title',
    descriptionKey: 'organization.sections.practiceDetails.description',
    routeName: 'organization-practice-details'
  },
  {
    // What the practice pays Scrivio is the payer's business, so unlike Praktijkgegevens this
    // is not offered to every member.
    id: 'subscription',
    group: 'organization',
    icon: Receipt,
    titleKey: 'organization.sections.subscription.title',
    descriptionKey: 'organization.sections.subscription.description',
    routeName: 'organization-subscription',
    permission: Permission.SUBSCRIPTION_READ
  },
  {
    id: 'locationManagement',
    group: 'organization',
    icon: MapPin,
    titleKey: 'organization.sections.locationManagement.title',
    descriptionKey: 'organization.sections.locationManagement.description',
    routeName: 'organization-locations',
    extraRoutes: ['organization-location-detail'],
    permission: Permission.LOCATIONS_READ,
    countKey: 'locations'
  },
  {
    // The one area that does not live under /organization: contracten is reached
    // at /contracts and always was. It is a settings screen all the same, so the
    // sidebar owns its routes too — otherwise opening Contracten from the sidebar
    // would be the one click that makes the sidebar disappear.
    id: 'contractManagement',
    group: 'organization',
    icon: FileSignature,
    titleKey: 'organization.sections.contractManagement.title',
    descriptionKey: 'organization.sections.contractManagement.description',
    routeName: 'contracts',
    extraRoutes: ['contract-create', 'contract-detail', 'contract-edit'],
    permission: Permission.CONTRACTS_READ,
    countKey: 'contracts'
  },
  {
    // No permission: every member reads it, and only editing asks for one.
    id: 'declareren',
    group: 'organization',
    icon: Receipt,
    titleKey: 'organization.sections.declareren.title',
    descriptionKey: 'organization.sections.declareren.description',
    routeName: 'organization-declareren'
  },
  {
    // Labels and default documents are governed by different permissions, and each
    // section of the page checks its own; the card is for a user holding either.
    id: 'documents',
    group: 'organization',
    icon: FileText,
    titleKey: 'organization.sections.documents.title',
    descriptionKey: 'organization.sections.documents.description',
    routeName: 'organization-documents',
    permission: [Permission.DOCUMENTS_MANAGE_LABELS, Permission.ORGANIZATION_SETTINGS_UPDATE]
  },
  {
    id: 'userManagement',
    group: 'people',
    icon: Users,
    titleKey: 'organization.sections.userManagement.title',
    descriptionKey: 'organization.sections.userManagement.description',
    routeName: 'organization-users',
    extraRoutes: ['organization-user-detail'],
    countKey: 'users'
  },
  {
    id: 'teamManagement',
    group: 'people',
    icon: UsersRound,
    titleKey: 'organization.sections.teamManagement.title',
    descriptionKey: 'organization.sections.teamManagement.description',
    routeName: 'organization-teams',
    extraRoutes: ['organization-team-detail'],
    countKey: 'teams'
  },
  {
    id: 'roleManagement',
    group: 'people',
    icon: ShieldCheck,
    titleKey: 'organization.sections.roleManagement.title',
    descriptionKey: 'organization.sections.roleManagement.description',
    routeName: 'organization-roles',
    permission: Permission.ROLES_READ,
    countKey: 'roles'
  },
  {
    // MFA decides who gets into the organisation, so it sits beside managing who is in it.
    id: 'security',
    group: 'people',
    icon: LockKeyhole,
    titleKey: 'organization.sections.security.title',
    descriptionKey: 'organization.sections.security.description',
    routeName: 'organization-security',
    permission: Permission.SECURITY_VIEW
  },
  {
    // The leveranciersdossier tells a practice to check Scrivio-support's access
    // here, so a card is what makes that sentence true (#4800).
    id: 'auditLog',
    group: 'people',
    icon: ScrollText,
    titleKey: 'organization.sections.auditLog.title',
    descriptionKey: 'organization.sections.auditLog.description',
    routeName: 'organization-audit-log',
    permission: Permission.AUDIT_READ
  },
  {
    id: 'clientStatuses',
    group: 'product',
    icon: ListChecks,
    titleKey: 'organization.sections.clientStatuses.title',
    descriptionKey: 'organization.sections.clientStatuses.description',
    routeName: 'organization-client-states',
    extraRoutes: ['organization-client-state-detail'],
    permission: Permission.CLIENT_STATES_READ,
    countKey: 'clientStates'
  },
  {
    id: 'appointmentTypes',
    group: 'product',
    icon: CalendarClock,
    titleKey: 'organization.sections.appointmentTypes.title',
    descriptionKey: 'organization.sections.appointmentTypes.description',
    routeName: 'organization-appointment-types',
    extraRoutes: ['organization-appointment-type-detail'],
    permission: Permission.APPOINTMENT_TYPES_READ,
    countKey: 'appointmentTypes'
  },
  {
    id: 'signals',
    group: 'product',
    icon: Bell,
    titleKey: 'organization.sections.signals.title',
    descriptionKey: 'organization.sections.signals.description',
    routeName: 'organization-signal-definitions',
    extraRoutes: ['organization-signal-authoring'],
    permission: Permission.SIGNAL_DEFINITIONS_MANAGE,
    countKey: 'signals'
  },
  {
    id: 'questionLibrary',
    group: 'dossier',
    icon: HelpCircle,
    titleKey: 'organization.sections.forms.questionLibrary.title',
    descriptionKey: 'organization.sections.forms.questionLibrary.description',
    routeName: 'organization-forms-questions',
    permission: Permission.QUESTIONS_MANAGE,
    countKey: 'questions'
  },
  {
    id: 'formsList',
    group: 'dossier',
    icon: ClipboardList,
    titleKey: 'organization.sections.forms.formsList.title',
    descriptionKey: 'organization.sections.forms.formsList.description',
    routeName: 'organization-forms-list',
    extraRoutes: ['organization-forms-detail', 'organization-form-settings'],
    permission: Permission.FORMS_MANAGE,
    countKey: 'forms'
  },
  {
    id: 'letterTemplates',
    group: 'dossier',
    icon: Mail,
    titleKey: 'organization.sections.letterTemplates.title',
    descriptionKey: 'organization.sections.letterTemplates.description',
    routeName: 'organization-letter-templates',
    extraRoutes: ['organization-letter-template-create', 'organization-letter-template-edit'],
    permission: Permission.LETTER_TEMPLATES_MANAGE,
    countKey: 'letterTemplates'
  },
  {
    id: 'epdImport',
    group: 'imports',
    icon: DatabaseBackup,
    titleKey: 'organization.sections.epdImport.title',
    descriptionKey: 'organization.sections.epdImport.description',
    routeName: 'organization-epd-import',
    permission: Permission.IMPORTS_EPD
  },
  {
    id: 'agendaImport',
    group: 'imports',
    icon: CalendarArrowUp,
    titleKey: 'organization.sections.agendaImport.title',
    descriptionKey: 'organization.sections.agendaImport.description',
    routeName: 'organization-agenda-import',
    permission: Permission.IMPORTS_AGENDA
  },
  {
    id: 'integrations',
    group: 'imports',
    icon: Plug,
    titleKey: 'organization.sections.integrations.title',
    descriptionKey: 'organization.sections.integrations.description',
    routeName: 'organization-integrations',
    permission: Permission.INTEGRATIONS_MANAGE
  }
]

/**
 * Settings routes no area owns: the sidebar renders, with nothing marked.
 *
 * The hub itself is here so entering Organisatiebeheer already shows the
 * sidebar. The other is a page reached from somewhere else — an import preview
 * belongs to whichever import produced it — and without this it would be a
 * settings screen with no way on to another.
 */
const UNLISTED_SETTINGS_ROUTES: readonly string[] = ['organization', 'organization-import-preview']

/** Every route the settings context answers for, mapped to the area that owns it (or `null`). */
const AREA_BY_ROUTE: Readonly<Record<string, string | null>> = {
  ...Object.fromEntries(UNLISTED_SETTINGS_ROUTES.map((name) => [name, null])),
  ...Object.fromEntries(
    ORGANIZATION_AREAS.flatMap((area) =>
      [area.routeName, ...(area.extraRoutes ?? [])].map((name) => [name, area.id] as const)
    )
  )
}

/**
 * Every route that shows the settings sidebar.
 *
 * Exported so `organization-routes-are-settings-routes.spec.ts` can assert that
 * no `/organization/...` page escapes it: a settings screen the context does not
 * name is one a practitioner can only leave through the hub, which is the round
 * trip this exists to remove.
 */
export const SETTINGS_ROUTE_NAMES: readonly string[] = Object.keys(AREA_BY_ROUTE)

/** One group as rendered: its heading and the areas this user may reach. */
export interface OrganizationAreaGroup {
  id: OrganizationGroupId
  titleKey: string
  areas: readonly OrganizationArea[]
}

/**
 * The grouped areas this user may reach.
 *
 * A group whose every area is permission-filtered away is absent rather than
 * empty — a bare heading over nothing is worse than the group not being there,
 * on the hub and in the sidebar alike. Grouping through a Map keeps
 * first-encounter order while still collecting an area declared out of sequence.
 *
 * Deliberately router-free: the hub renders these without asking which route is
 * open, and only {@link useOrganizationSettingsNav} needs a route.
 */
export function useOrganizationAreaGroups() {
  const { hasPermission } = usePermissions()

  const groups = computed<OrganizationAreaGroup[]>(() => {
    const byGroup = new Map<OrganizationGroupId, OrganizationArea[]>()

    for (const area of ORGANIZATION_AREAS) {
      if (area.permission && ![area.permission].flat().some((p) => hasPermission(p))) continue
      const areas = byGroup.get(area.group) ?? []
      areas.push(area)
      byGroup.set(area.group, areas)
    }

    return [...byGroup].map(([id, areas]) => ({
      id,
      titleKey: ORGANIZATION_GROUP_TITLE_KEYS[id],
      areas
    }))
  })

  return { groups }
}

/**
 * The settings sidebar's model: the same groups, plus which settings screen is
 * open and which area owns it.
 */
export function useOrganizationSettingsNav() {
  const route = useRoute()
  const { groups } = useOrganizationAreaGroups()

  /** Whether a settings screen is open. */
  const isActive = computed(() => (route.name as string) in AREA_BY_ROUTE)

  /**
   * The open area's id, or `null` on a settings screen no area owns.
   *
   * Resolved against every declared area rather than the filtered groups, so the
   * marking does not depend on the filter agreeing — a route the guard let
   * through is one the user may reach.
   */
  const activeAreaId = computed<string | null>(() => AREA_BY_ROUTE[route.name as string] ?? null)

  return { isActive, activeAreaId, groups }
}
